/**
 * Toast notification system for Proctoring SDK
 * Manages stacking, auto-dismiss, and animations
 */

const icons = {
    warning: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>`,
    error: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="15" y1="9" x2="9" y2="15"/>
        <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>`,
    info: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>`,
    success: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>`,
    close: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>`,
};

class Notifications {
    container = null;
    shadowRoot = null;
    notifications = [];
    options = {
        duration: 5000,
        maxVisible: 3,
        position: 'top-right',
    };

    init(shadowRoot, options = {}) {
        this.shadowRoot = shadowRoot;
        this.options = { ...this.options, ...options };

        this.container = document.createElement('div');
        this.container.className = `notifications-container pos-${this.options.position}`;
        this.shadowRoot.appendChild(this.container);
    }

    show(title, message, type = 'info') {
        // Limit visible notifications
        while (this.notifications.length >= this.options.maxVisible) {
            this.dismiss(this.notifications[0].id);
        }

        const id = Date.now() + Math.random();
        const notification = this.createNotification(id, title, message, type);

        this.notifications.push({ id, element: notification });
        this.container.appendChild(notification);

        // Auto-dismiss
        if (this.options.duration > 0) {
            setTimeout(() => this.dismiss(id), this.options.duration);
        }

        return id;
    }

    createNotification(id, title, message, type) {
        const notification = document.createElement('div');
        notification.className = `notification ${type}`;
        notification.dataset.id = id;

        notification.innerHTML = `
            <span class="notification-icon">${icons[type] || icons.info}</span>
            <div class="notification-content">
                <div class="notification-title">${this.escapeHtml(title)}</div>
                <div class="notification-message">${this.escapeHtml(message)}</div>
            </div>
            <button class="notification-close" aria-label="Close">${icons.close}</button>
        `;

        notification.querySelector('.notification-close').addEventListener('click', () => {
            this.dismiss(id);
        });

        return notification;
    }

    dismiss(id) {
        const index = this.notifications.findIndex((n) => n.id === id);
        if (index === -1) return;

        const { element } = this.notifications[index];
        element.classList.add('exiting');

        element.addEventListener('animationend', () => {
            element.remove();
        });

        this.notifications.splice(index, 1);
    }

    dismissAll() {
        [...this.notifications].forEach((n) => this.dismiss(n.id));
    }

    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    destroy() {
        this.dismissAll();
        this.container?.remove();
        this.container = null;
        this.notifications = [];
    }
}

export default new Notifications();
